import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface ClientWorkTeamCreateDTO {
  id?: number;
  client: number;
  totalStartDate?: string;
  recurrentServicesAccountingStartDate?: string;
  accountingStartDate?: string;
  recurrentServicesLaborStartDate?: string;
  laborStartDate?: string;
  recurrentServicesFiscalStartDate?: string;
  fiscalStartDate?: string;
  observations?: string;
  accountingResponsible?: number;
  laborResponsible?: number;
  fiscalResponsible?: number;
  billingResponsible?: number;
}

export interface ClientWorkTeamResponse {
  id: number;
  created_at: string;
  updated_At: string;
  totalStartDate?: string;
  recurrentServicesAccountingStartDate?: string;
  accountingStartDate?: string;
  recurrentServicesLaborStartDate?: string;
  laborStartDate?: string;
  recurrentServicesFiscalStartDate?: string;
  fiscalStartDate?: string;
  observations?: string;
  accountingCollaborators?: number[];
  laborCollaborators?: number[];
  fiscalCollaborators?: number[];
  client: {
    id: number;
    name: string;
    code: number;
    personType: string;
    [key: string]: any;
  };
  accountingResponsible?: {
    id: number;
    name: string;
    email: string;
    department: string;
    [key: string]: any;
  };
  laborResponsible?: {
    id: number;
    name: string;
    email: string;
    department: string;
    [key: string]: any;
  };
  fiscalResponsible?: {
    id: number;
    name: string;
    email: string;
    department: string;
    [key: string]: any;
  };
  billingResponsible?: {
    id: number;
    name: string;
    email: string;
    department: string;
    [key: string]: any;
  } | null;
}

export interface CollaboratorResponse {
  userId: number;
  userName: string;
  userEmail: string;
}

export interface CollaboratorArea {
  ACCOUNTING: 'ACCOUNTING';
  LABOR: 'LABOR';
  FISCAL: 'FISCAL';
}

@Injectable({
  providedIn: 'root'
})
export class ClientWorkTeamsService {

  constructor(
    private http: HttpClient,
  ) { }

  /**
   * Crear o actualizar un equipo de trabajo
   */
  async createOrUpdate(workTeam: ClientWorkTeamCreateDTO): Promise<ClientWorkTeamResponse> {
    const url = workTeam.id 
      ? `${environment.apiUrl}/client-work-teams/${workTeam.id}`
      : `${environment.apiUrl}/client-work-teams`;
    
    const method = workTeam.id ? 'PUT' : 'POST';
    
    return await lastValueFrom(this.http.request<ClientWorkTeamResponse>(method, url, {
      body: workTeam
    }));
  }

  /**
   * Obtener equipo de trabajo por cliente
   */
  async getByClient(clientId: number): Promise<ClientWorkTeamResponse | null> {
    return await lastValueFrom(this.http.get<ClientWorkTeamResponse>(`${environment.apiUrl}/client-work-teams/client/${clientId}`));
  }

  /**
   * Agregar colaborador a un área específica
   */
  async addCollaborator(
    workTeamId: number, 
    area: 'ACCOUNTING' | 'LABOR' | 'FISCAL', 
    userId: number
  ) {
    return await lastValueFrom(
      this.http.post(`${environment.apiUrl}/client-work-teams/${workTeamId}/collaborators/${area}/${userId}`, {})
    );
  }

  /**
   * Eliminar colaborador de un área específica
   */
  async removeCollaborator(
    workTeamId: number, 
    area: 'ACCOUNTING' | 'LABOR' | 'FISCAL', 
    userId: number
  ) {
    return await lastValueFrom(
      this.http.delete(`${environment.apiUrl}/client-work-teams/${workTeamId}/collaborators/${area}/${userId}`)
    );
  }

  /**
   * Obtener colaboradores de un área específica
   */
  async getCollaboratorsByArea(
    workTeamId: number, 
    area: 'ACCOUNTING' | 'LABOR' | 'FISCAL'
  ): Promise<CollaboratorResponse[]> {
    return await lastValueFrom(
      this.http.get<CollaboratorResponse[]>(`${environment.apiUrl}/client-work-teams/${workTeamId}/collaborators/${area}`)
    );
  }

  /**
   * Obtener todos los colaboradores de un equipo de trabajo
   */
  async getAllCollaborators(workTeamId: number): Promise<CollaboratorResponse[]> {
    return await lastValueFrom(
      this.http.get<CollaboratorResponse[]>(`${environment.apiUrl}/client-work-teams/${workTeamId}/collaborators`)
    );
  }

  /**
   * Establecer colaboradores para un área específica
   */
  async setCollaboratorsForArea(
    workTeamId: number, 
    area: 'ACCOUNTING' | 'LABOR' | 'FISCAL', 
    userIds: number[]
  ) {
    return await lastValueFrom(
      this.http.put(`${environment.apiUrl}/client-work-teams/${workTeamId}/collaborators/${area}`, {
        userIds: userIds
      })
    );
  }
}
